import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CalendarToday,
  Chat,
  Check,
  ChevronRight,
  Close,
  Description,
  OpenInNew,
  Person,
  Psychology,
} from '@mui/icons-material';
import { Badge, Button, Card, CardContent, CardHeader } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { CardTitle } from '../components/card-title';
import { DataTable } from '../components/data-table';
import { DebugCard } from '../components/debug-card';
import { EncounterAnalysisCard } from '../components/encounter-analysis-card';
import { EncounterTelemetryCard } from '../components/encounter-telemetry-card';
import { LoadingState } from '../components/loading-state';
import {
  useGetEncounterDetailsQuery,
  useGetProviderNoteProcessingSessionsQuery,
} from '../store/api';
import { formatDateTime } from '../utils/formatting';

// Badge color helper function
const getStatusBadgeColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'success':
    case 'finished':
      return 'bg-green-100 text-green-800';
    case 'pending':
    case 'in_progress':
    case 'processing':
      return 'bg-yellow-100 text-yellow-800';
    case 'failed':
    case 'error':
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function EncounterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const encounterId = id || ''; // Keep as string since it's a UUID

  // Always call the encounter details query
  const { data, isLoading } = useGetEncounterDetailsQuery({ encounterId }, { skip: !encounterId });

  // Extract providerId from the encounter response
  const providerId = data?.provider?.provider_id || data?.encounter?.provider_id;

  // Get related note processing sessions for this encounter
  const { data: npsData } = useGetProviderNoteProcessingSessionsQuery(
    {
      providerId: providerId || '',
      encounterId: encounterId,
      perPage: 10,
    },
    { skip: !providerId || !encounterId },
  );

  // Use provider data from encounter response
  const currentUser = data?.provider;

  // If we don't have the required encounterId, show error
  if (!encounterId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Encounter not found</h2>
            <p className="text-gray-600 mt-2">Invalid encounter ID.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading encounter details..." />;
  }

  if (!data?.encounter) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Encounter not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const { encounter, provider, appointment, transcriptions, notes } = data;

  const transcriptionsColumns = [
    {
      key: 'transcription_session_id',
      label: 'Session ID',
      render: (value: string) => (
        <span className="text-sm font-medium text-blue-600">{value.substring(0, 8)}...</span>
      ),
    },
    {
      key: 'start_time',
      label: 'Started',
      render: (value: string) => (
        <span className="text-sm text-gray-900">{value ? formatDateTime(value) : 'N/A'}</span>
      ),
    },
    {
      key: 'chunk_count',
      label: 'Chunks',
      render: (value: number) => <span className="text-sm text-gray-500">{value}</span>,
    },
    {
      key: 'job_status',
      label: 'Status',
      render: (value: string) => (
        <Badge className={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'notes_created',
      label: 'Notes',
      render: (value: number) => <span className="text-sm text-blue-600">{value} notes</span>,
    },
  ];

  const notesColumns = [
    {
      key: 'note_id',
      label: 'Note ID',
      render: (value: string) => <span className="text-sm font-medium text-blue-600">{value}</span>,
    },
    {
      key: 'template',
      label: 'Template',
      render: (value: string) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'encounter_type',
      label: 'Encounter Type',
      render: (value: string) => <span className="text-sm text-gray-700">{value || 'N/A'}</span>,
    },
    {
      key: 'patient_name',
      label: 'Patient',
      render: (value: string) => <span className="text-sm text-gray-700">{value || 'N/A'}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge className={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'has_feedback',
      label: 'Feedback',
      render: (value: boolean) =>
        value ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Close className="h-4 w-4 text-red-500" />
        ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton />
        </div>
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            {providerId && (
              <li>
                <div className="flex items-center">
                  <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                  <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                    <Button
                      variant="text"
                      className="text-sm font-medium text-gray-700 hover:text-blue-600"
                    >
                      {currentUser?.name || 'Provider'}
                    </Button>
                  </Link>
                </div>
              </li>
            )}
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                  Encounter #{encounter.encounter_id}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Encounter Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <Description className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Encounter #{encounter.encounter_id}
                  </h1>
                  <p className="text-gray-600">Patient: {encounter.patient_id}</p>
                  <p className="text-sm text-gray-500">
                    Created: {formatDateTime(encounter.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {encounter.organization_id && (
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Organization</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {encounter.organization_id}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Telemetry Summary */}
        <div className="mt-6 mb-6">
          <EncounterTelemetryCard encounterId={encounterId} />
        </div>

        {/* Encounter Analysis */}
        {encounter.encounter_analysis && (
          <div className="mt-6 mb-6">
            <EncounterAnalysisCard analysis={encounter.encounter_analysis} />
          </div>
        )}

        {/* Debug Card */}
        <DebugCard
          transcriptionId={transcriptions?.[0]?.transcription_session_id}
          noteProcessingId={npsData?.items?.[0]?.note_processing_session_id}
          noteId={notes?.[0]?.note_id}
        />

        {/* Related Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Provider Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Person className="mr-2 h-5 w-5" />
                Provider Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Name:</span>
                  <span className="text-sm text-gray-900">
                    {provider?.name || currentUser?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Email:</span>
                  <span className="text-sm text-gray-900">
                    {provider?.email || currentUser?.email}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Provider ID:</span>
                  <span className="text-sm text-gray-900">
                    {provider?.provider_id ||
                      (currentUser as any)?.provider_id ||
                      (currentUser as any)?.userId}
                  </span>
                </div>
                {providerId && (
                  <div className="mt-4">
                    <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                      <Button variant="outlined" size="small" className="w-full">
                        View Provider Details
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Appointment Information */}
          {appointment ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CalendarToday className="mr-2 h-5 w-5" />
                  Associated Appointment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Appointment ID:</span>
                    <span className="text-sm text-gray-900">{appointment.appointment_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Type:</span>
                    <span className="text-sm text-gray-900">{appointment.encounter_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Scheduled:</span>
                    <span className="text-sm text-gray-900">
                      {formatDateTime(appointment.start_time)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Status:</span>
                    <Badge className={getStatusBadgeColor(appointment.status)}>
                      {appointment.status}
                    </Badge>
                  </div>
                  {appointment.organization_id && (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Organization:</span>
                      <span className="text-sm text-gray-900">{appointment.organization_id}</span>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <Link to={`/admin-v2/appointment/${appointment.appointment_id}`}>
                    <Button variant="outlined" size="small" className="w-full">
                      View Appointment Details
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CalendarToday className="mr-2 h-5 w-5" />
                  Associated Appointment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  This encounter is not associated with an appointment.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Chat Session Information */}
          {encounter.streaming_thread_id ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Chat className="mr-2 h-5 w-5" />
                  Chat Session
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Thread ID:</span>
                    <span className="text-sm text-gray-900">
                      {encounter.streaming_thread_id.substring(0, 8)}...
                    </span>
                  </div>
                  <div className="mt-4">
                    <Link to={`/admin-v2/streaming-thread/${encounter.streaming_thread_id}`}>
                      <Button variant="outlined" size="small" className="w-full">
                        View Chat Session
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Chat className="mr-2 h-5 w-5" />
                  Chat Session
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  No chat session associated with this encounter.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Encounter Details */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Encounter Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Patient ID</div>
                <p className="text-lg font-semibold text-gray-900">{encounter.patient_id}</p>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Organization ID</div>
                <p className="text-lg font-semibold text-gray-900">
                  {encounter.organization_id || 'Not specified'}
                </p>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Created At</div>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDateTime(encounter.created_at)}
                </p>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Appointment ID</div>
                <p className="text-lg font-semibold text-gray-900">
                  {encounter.appointment_id ? `APT_${encounter.appointment_id}` : 'Not associated'}
                </p>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Input Documents</div>
                <p
                  className={`text-lg font-semibold ${encounter.input_documents === 0 ? 'text-red-600' : 'text-gray-900'}`}
                >
                  {encounter.input_documents || 0} input docs
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Note Processing Sessions Information */}
        {npsData && npsData.items && npsData.items.length > 0 && (
          <div className="bg-white rounded-lg shadow mt-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Psychology className="mr-2 h-5 w-5" />
                Note Processing Sessions ({npsData.items.length})
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {npsData.items.map((nps: any) => (
                  <div key={nps.note_processing_session_id} className="border rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500">Session ID:</span>
                        <span className="text-sm text-gray-900">
                          {nps.note_processing_session_id.substring(0, 8)}...
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500">Status:</span>
                        <Badge className={getStatusBadgeColor(nps.status)}>
                          {nps.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500">Transcripts:</span>
                        <span className="text-sm text-gray-900">
                          {nps.transcription_session_ids?.length || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500">Note:</span>
                        <span className="text-sm text-gray-900">
                          {nps.note_id ? `${nps.note_id.substring(0, 8)}...` : 'No note'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500">Created:</span>
                        <span className="text-sm text-gray-900">
                          {formatDateTime(nps.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Link
                        to={`/admin-v2/note-processing-session/${nps.note_processing_session_id}`}
                      >
                        <Button variant="outlined" size="small">
                          View Session Details
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Related Data Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <DataTable
            title="Transcriptions"
            columns={transcriptionsColumns}
            data={transcriptions || []}
            loading={!transcriptions}
            getRowUrl={() => ''}
            emptyMessage="Coming Soon! 😘"
          />

          <DataTable
            title="Notes"
            columns={notesColumns}
            data={notes || []}
            loading={!notes}
            getRowUrl={note => {
              const noteId = note.note_id || note.noteId || note.id;
              return noteId ? `/admin-v2/note/${noteId}` : '';
            }}
            emptyMessage="No notes found for this encounter"
          />
        </div>
      </main>
    </div>
  );
}
